/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Tabs,
  Tab,
  TablePagination,
  Skeleton
} from '@mui/material';
import {
  Visibility,
  LocationOn
} from '@mui/icons-material';
import { getTrips } from '../services/graphql';
import { formatCurrency } from '../utils/format';

function TripManagement() {
  const [trips, setTrips] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState<any | null>(null);

  useEffect(() => {
    getTrips().then(data => {
      setTrips(data);
      setLoading(false);
    });
  }, []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'completed': return 'success';
      case 'in_progress':
      case 'accepted':
      case 'picking_up': return 'info';
      case 'cancelled': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'cash': return '💵';
      case 'card': return '💳';
      case 'mobile_money': return '📱';
      default: return '💰';
    }
  };

  // Terminal statuses — everything else is an in-flight/active trip
  const CANCELLED_STATUSES = ['CANCELLED', 'CANCELLED_BY_RIDER', 'CANCELLED_BY_DRIVER', 'EXPIRED', 'RIDER_NO_SHOW', 'DRIVER_NO_SHOW', 'NO_DRIVERS_AVAILABLE'];
  const TERMINAL_STATUSES = ['COMPLETED', ...CANCELLED_STATUSES];

  const getFilteredTrips = () => {
    switch (tabValue) {
      case 0: return trips.filter(trip => !TERMINAL_STATUSES.includes(trip.status));
      case 1: return trips.filter(trip => trip.status === 'COMPLETED');
      case 2: return trips.filter(trip => CANCELLED_STATUSES.includes(trip.status));
      default: return trips;
    }
  };

  const filteredTrips = getFilteredTrips();
  const paginatedTrips = filteredTrips.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewTrip = (trip: any) => {
    setSelectedTrip(trip);
    setDialogOpen(true);
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Trip Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Active Trips" />
          <Tab label="Completed Trips" />
          <Tab label="Cancelled Trips" />
        </Tabs>
      </Box>

      <Card elevation={0}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trip ID</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Passenger</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Fare</TableCell>
                  <TableCell>Distance</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={100} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={40} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  paginatedTrips.map((trip) => (
                  <TableRow key={trip.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontFamily="monospace">
                          {trip.id}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(trip.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {trip.driverName?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body2">{trip.driverName || 'Unassigned'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {trip.riderName?.charAt(0) || 'R'}
                        </Avatar>
                        <Typography variant="body2">{trip.riderName || trip.riderId}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {trip.pickup?.address || 'N/A'} → {trip.destination?.address || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={trip.status} 
                        color={getStatusColor(trip.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(trip.finalFare || trip.fareEstimate || 0)}
                      </Typography>
                      {!trip.finalFare && trip.fareEstimate > 0 && (
                        <Typography variant="caption" color="textSecondary">(est.)</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{trip.distance?.toFixed(1) || '0.0'} km</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDuration(trip.duration || 0)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getPaymentMethodIcon(trip.paymentMethod?.toLowerCase() || 'cash')} {trip.paymentMethod || 'CASH'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(trip.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewTrip(trip)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTrips.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Trip Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Trip Details</DialogTitle>
        <DialogContent>
          {selectedTrip && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Trip Information</Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2">Trip ID</Typography>
                  <Typography fontFamily="monospace">{selectedTrip.id}</Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip 
                    label={selectedTrip.status.toUpperCase()} 
                    color={getStatusColor(selectedTrip.status) as any}
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2">Created</Typography>
                  <Typography>{new Date(selectedTrip.createdAt).toLocaleString()}</Typography>
                </Box>
                {selectedTrip.completedAt && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Completed</Typography>
                    <Typography>{new Date(selectedTrip.completedAt).toLocaleString()}</Typography>
                  </Box>
                )}
                {selectedTrip.cancelledAt && (
                  <>
                    <Box mb={2}>
                      <Typography variant="subtitle2">Cancelled</Typography>
                      <Typography>{new Date(selectedTrip.cancelledAt).toLocaleString()}</Typography>
                    </Box>
                    {selectedTrip.cancellationReason && (
                      <Box mb={2}>
                        <Typography variant="subtitle2">Cancellation Reason</Typography>
                        <Typography>{selectedTrip.cancellationReason}</Typography>
                      </Box>
                    )}
                  </>
                )}
                {selectedTrip.startedAt && (
                  <Box mb={2}>
                    <Typography variant="subtitle2">Started</Typography>
                    <Typography>{new Date(selectedTrip.startedAt).toLocaleString()}</Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Participants</Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2">Driver</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar>{selectedTrip.driverName?.charAt(0) || 'U'}</Avatar>
                    <Typography>{selectedTrip.driverName || 'Unassigned'}</Typography>
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2">Rider</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar>{selectedTrip.riderName?.charAt(0) || 'R'}</Avatar>
                    <Typography>{selectedTrip.riderName || selectedTrip.riderId}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Route Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="success.main">
                        <LocationOn fontSize="small" /> Pickup Location
                      </Typography>
                      <Typography>{selectedTrip.pickup?.address || 'N/A'}</Typography>
                      {selectedTrip.pickup?.latitude && (
                        <Typography variant="caption" color="textSecondary">
                          {selectedTrip.pickup.latitude.toFixed(6)}, {selectedTrip.pickup.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="error.main">
                        <LocationOn fontSize="small" /> Destination
                      </Typography>
                      <Typography>{selectedTrip.destination?.address || 'N/A'}</Typography>
                      {selectedTrip.destination?.latitude && (
                        <Typography variant="caption" color="textSecondary">
                          {selectedTrip.destination.latitude.toFixed(6)}, {selectedTrip.destination.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Trip Metrics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Box textAlign="center">
                      <Typography variant="subtitle2">Fare</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(selectedTrip.finalFare || selectedTrip.fareEstimate || 0)}
                      </Typography>
                      {!selectedTrip.finalFare && selectedTrip.fareEstimate > 0 && (
                        <Typography variant="caption" color="warning.main">
                          Estimated (final fare pending)
                        </Typography>
                      )}
                      {selectedTrip.finalFare > 0 && selectedTrip.fareEstimate > 0 && selectedTrip.finalFare !== selectedTrip.fareEstimate && (
                        <Typography variant="caption" color="textSecondary">
                          Est: {formatCurrency(selectedTrip.fareEstimate)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box textAlign="center">
                      <Typography variant="subtitle2">Distance</Typography>
                      <Typography variant="h6">
                        {selectedTrip.distance?.toFixed(1) || '0.0'} km
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box textAlign="center">
                      <Typography variant="subtitle2">Duration</Typography>
                      <Typography variant="h6">
                        {formatDuration(selectedTrip.duration || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box textAlign="center">
                      <Typography variant="subtitle2">Payment</Typography>
                      <Typography variant="h6">
                        {getPaymentMethodIcon(selectedTrip.paymentMethod?.toLowerCase() || 'cash')} {selectedTrip.paymentMethod || 'CASH'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TripManagement;